// Small rules shared by every format.
import type { Option, Reveal } from '@shared/types';
import type { AnswerPhase } from './types';

/**
 * Options sit in a 2×2 grid of tiles when every one is short enough to read on a tile; otherwise full-width rows.
 * A word too long for half the screen would have to break in the middle (phones' browsers do not reliably hyphenate),
 * so any such word sends the options to rows too: `maxWord` is the longest word, in characters, a tile can hold.
 */
export const TILE_MAX_CHARS = 32;
export function tilesFor(options: ReadonlyArray<Option>, maxWord = Infinity): boolean {
  return options.length > 0 && options.every((o) => o.text.trim().length <= TILE_MAX_CHARS && longestWord(o.text).length <= maxWord);
}

/** The longest run of letters and digits in a text (hyphens and slashes already give a line a place to break). */
export function longestWord(text: string): string {
  return text.split(/[\s\-‐–—/]+/).reduce((a, w) => (w.length > a.length ? w : a), '');
}

/**
 * The player page is one column at most 448 px wide with 16 px gutters; a tile is half of it less a 12 px gap, and its
 * text has 32 px less than that (padding and border). Medical words in the interface face at 17 px measure up to
 * about 10.3 px a letter, so this is a safe count of letters for a tile's text when nothing can be measured.
 */
export const TILE_PX_PER_CHAR = 10.3;
export function tileTextWidth(viewportWidth: number): number {
  const column = Math.min(448, Math.max(240, viewportWidth)) - 32;
  return (column - 12) / 2 - 32;
}
export function tileWordBudget(viewportWidth: number): number {
  return Math.max(6, Math.floor(tileTextWidth(viewportWidth) / TILE_PX_PER_CHAR));
}

/** The visual state of one answer. Matches the states OptionButton draws. */
export type AnswerState = 'idle' | 'locked' | 'dim' | 'correct' | 'wrong';

/** The phase the answer area should draw: a pick always shows as locked, a reveal needs its data. */
export function effectivePhase(phase: AnswerPhase, myOptionId?: string, reveal?: Reveal): AnswerPhase {
  return phaseFor(phase, Boolean(myOptionId), reveal);
}
/** The same rule for any answer (a value, an order, several picks). */
export function phaseFor(phase: AnswerPhase, answered: boolean, reveal?: Reveal): AnswerPhase {
  if (phase === 'revealed') return reveal ? 'revealed' : answered ? 'locked' : 'answering';
  if (phase === 'answering' && answered) return 'locked';
  return phase;
}

export function answerState(id: string, phase: AnswerPhase, myOptionId?: string, reveal?: Reveal): AnswerState {
  if (phase === 'revealed' && reveal) return id === reveal.correctId ? 'correct' : id === myOptionId ? 'wrong' : 'dim';
  if (myOptionId) return id === myOptionId ? 'locked' : 'dim';
  return 'idle';
}

/** The same for a question with several right answers (hotspot, multi): every right one is marked at the reveal. */
export function answerStateAny(id: string, phase: AnswerPhase, myOptionId: string | undefined, reveal: Reveal | undefined, right: readonly string[]): AnswerState {
  if (phase === 'revealed' && reveal) return right.includes(id) ? 'correct' : id === myOptionId ? 'wrong' : 'dim';
  if (myOptionId) return id === myOptionId ? 'locked' : 'dim';
  return 'idle';
}

/** Share of the room that chose an option, as a whole percentage. */
export function sharePct(reveal: Pick<Reveal, 'counts' | 'answered'> | undefined, id: string): number {
  if (!reveal || !reveal.answered) return 0;
  return Math.round((100 * (reveal.counts[id] ?? 0)) / reveal.answered);
}

/** True or false tiles: which option is "True". Ids T/F from the set builder, else the text, else the order. */
export function tfKind(o: Option, index: number): 'true' | 'false' {
  const id = o.id.trim().toUpperCase();
  if (id === 'T') return 'true';
  if (id === 'F') return 'false';
  const t = o.text.trim().toLowerCase();
  if (t === 'true') return 'true';
  if (t === 'false') return 'false';
  return index === 0 ? 'true' : 'false';
}
